import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { auditLogKeys } from '@/features/audit-log';

import {
  createGlobalDiscount,
  getGlobalDiscounts,
  setGlobalDiscountActive,
  setGlobalDiscountBanner,
  updateGlobalDiscount,
} from './global-discount.api';
import type { GlobalDiscountInput } from '../types/global-discount.types';

export const globalDiscountKeys = {
  all: ['global-discounts'] as const,
  lists: () => [...globalDiscountKeys.all, 'list'] as const,
  list: (countryId: number) => [...globalDiscountKeys.lists(), countryId] as const,
};

export function useGlobalDiscounts(countryId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: globalDiscountKeys.list(countryId ?? 0),
    queryFn: ({ signal }) => getGlobalDiscounts(countryId!, signal),
    enabled: enabled && countryId !== undefined,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: globalDiscountKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
  };
}

export function useCreateGlobalDiscount(countryId: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: GlobalDiscountInput) => createGlobalDiscount(countryId, input),
    onSuccess: () => {
      toast.success('Global discount created.');
      invalidate();
    },
  });
}

export function useUpdateGlobalDiscount() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { id: number; values: GlobalDiscountInput }) =>
      updateGlobalDiscount(input.id, input.values),
    onSuccess: (result) => {
      toast.success(result.changed > 0 ? 'Global discount updated.' : 'Nothing changed.');
      invalidate();
    },
  });
}

export function useSetGlobalDiscountActive() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { id: number; active: boolean }) =>
      setGlobalDiscountActive(input.id, input.active),
    onSuccess: (_, input) => {
      toast.success(
        input.active ? 'Global discount switched on.' : 'Global discount switched off.',
      );
      invalidate();
    },
  });
}

export function useSetGlobalDiscountBanner() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { id: number; on: boolean }) => setGlobalDiscountBanner(input.id, input.on),
    onSuccess: (_, input) => {
      toast.success(input.on ? 'Banner switched on.' : 'Banner switched off.');
      invalidate();
    },
  });
}
